import React from "react";
import { MapPin, Sparkles, Calendar } from "lucide-react";
import { photoUrl } from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function toAbsolute(url) {
    if (!url) return null;
    if (url.startsWith("http")) return url;
    if (url.startsWith("/api/photos/")) return photoUrl(url.replace("/api/photos/", ""));
    return `${BACKEND_URL}${url}`;
}

export function ListingCard({ listing, onClick, testId }) {
    const cover = toAbsolute(listing.cover_url);
    return (
        <button
            onClick={onClick}
            data-testid={testId}
            className="w-full text-left rounded-3xl overflow-hidden border border-border bg-card lift"
        >
            <div className="relative aspect-[16/10] bg-gradient-to-br from-primary/20 to-accent">
                {cover && <img src={cover} alt={listing.title} className="absolute inset-0 h-full w-full object-cover" />}
                <div className="absolute top-3 left-3 rounded-full bg-background/90 backdrop-blur px-3 py-1.5 text-sm font-black">
                    <span className="text-gradient-purple">₹{(listing.rent || 0).toLocaleString("en-IN")}</span>
                    <span className="text-muted-foreground text-xs font-semibold"> /mo</span>
                </div>
                {listing.gender_preference && listing.gender_preference !== "Any" && (
                    <div className="absolute top-3 right-3 rounded-full bg-background/90 backdrop-blur px-2.5 py-1 text-[10px] font-bold">
                        {listing.gender_preference}
                    </div>
                )}
            </div>
            <div className="p-4">
                <div className="font-display text-lg font-bold leading-tight">{listing.title}</div>
                <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                        <MapPin className="h-3 w-3" strokeWidth={2.5} /> {listing.city}
                    </span>
                    <span>·</span>
                    <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" strokeWidth={2.5} /> {listing.move_in_date}
                    </span>
                </div>
                {!!listing.amenities?.length && (
                    <div className="mt-2 flex flex-wrap gap-1">
                        {listing.amenities.slice(0, 4).map((a) => (
                            <span key={a} className="text-[10px] font-semibold uppercase tracking-wide rounded-full bg-accent text-accent-foreground px-2 py-0.5">
                                {a.replace(/_/g, " ")}
                            </span>
                        ))}
                    </div>
                )}
            </div>
        </button>
    );
}

export function RoommateCard({ profile, onClick, testId }) {
    const cover = profile.picture || (profile.photo_urls?.[0] ? toAbsolute(profile.photo_urls[0]) : null);
    return (
        <button
            onClick={onClick}
            data-testid={testId}
            className="w-full text-left rounded-3xl overflow-hidden border border-border bg-card lift"
        >
            <div className="relative aspect-[4/3] bg-gradient-to-br from-primary/20 to-accent">
                {cover && <img src={cover} alt={profile.name} className="absolute inset-0 h-full w-full object-cover" />}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <div className="absolute top-3 left-3 rounded-full bg-background/90 backdrop-blur px-3 py-1.5 flex items-center gap-1">
                    <Sparkles className="h-3 w-3 text-primary" strokeWidth={3} />
                    <span className="font-black text-sm text-gradient-purple">{profile.match_percent ?? 0}%</span>
                </div>
                <div className="absolute bottom-2 left-3 right-3 text-white">
                    <div className="font-display text-lg font-bold leading-tight truncate">
                        {profile.name}
                        {profile.age ? <span className="font-normal">, {profile.age}</span> : null}
                    </div>
                    {profile.city && (
                        <div className="flex items-center gap-1 text-xs opacity-90">
                            <MapPin className="h-3 w-3" strokeWidth={2.5} /> {profile.city}
                        </div>
                    )}
                </div>
            </div>
            {!!profile.vibe_tags?.length && (
                <div className="p-3 flex flex-wrap gap-1.5">
                    {profile.vibe_tags.slice(0, 3).map((t) => (
                        <span key={t} className="vibe-pill text-[11px] py-0.5 px-2">{t}</span>
                    ))}
                    {profile.vibe_tags.length > 3 && (
                        <span className="vibe-pill text-[11px] py-0.5 px-2">+{profile.vibe_tags.length - 3}</span>
                    )}
                </div>
            )}
        </button>
    );
}
